import { AxiosInstance } from 'axios';
import { ArgumentValidator } from '../../helpers';
import { QueryStringBuilder } from '@/shared/api-client/helpers';
import { IEncryptionKey, IPagedList, IPagination, ITinyElection } from '../models';
import { IActivity, IActivityElection, IActivityToElectionReference, IEditableActivity, INewActivity, ITemporaryMotioningAccessTicket } from '../models/activity';
import { IMotionDocumentType } from '@/shared/api-client/models/activity/motion-document-type';
import { ActivityType } from '../enums/activity-type';

export class ActivityClient {

    constructor(private axiosClient: AxiosInstance, private baseUrl: string) {

    }

    public get apiUrl() {
        return `/activity`;
    }

    public async list(pagination?: IPagination, staleId?: string, type?: ActivityType, includeArchived = false): Promise<IPagedList<IActivity>> {

        try {

            const url = new QueryStringBuilder()
                .withPaginationIfNotEmpty(pagination)
                .withQueryIfNotEmpty('staleId', staleId)
                .withQueryIfNotEmpty('includeArchived', includeArchived)
                .withQueryIfNotEmpty('type', type)
                .build(`${this.apiUrl}/`);

            const response = await this.axiosClient.get(url);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async get(id: string, motioningAccessTicketId?: string): Promise<IActivity> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        const url = new QueryStringBuilder()
            .withQueryIfNotEmpty('motioningAccessTicketId', motioningAccessTicketId)
            .build(`${this.apiUrl}/${id}`);

        try {

            const response = await this.axiosClient.get(
                url
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async create(activity: INewActivity): Promise<IActivity> {

        ArgumentValidator.notNullOrEmpty(activity, `activity`);

        try {
            const response = await this.axiosClient.post(
                `${this.apiUrl}`,
                activity,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async update(id: string, activity: IEditableActivity): Promise<IActivity> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        ArgumentValidator.notNullOrEmpty(activity, `activity`);

        try {
            const response = await this.axiosClient.put(
                `${this.apiUrl}/${id}`,
                activity,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async archive(id: string): Promise<void> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        const response = await this.axiosClient.patch(
            `${this.apiUrl}/${id}/archive`
        );

        return response.data;
    }

    public async unArchive(id: string): Promise<void> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        const response = await this.axiosClient.patch(
            `${this.apiUrl}/${id}/unarchive`
        );

        return response.data;
    }

    public async delete(id: string): Promise<void> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        const response = await this.axiosClient.delete(`${this.apiUrl}/${id}`);

        return response.data;
    }

    public async getElections(id: string, agendaItemId?: string, pagination?: IPagination, staleId?: string): Promise<IPagedList<IActivityElection<ITinyElection>>> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        try {

            const url = new QueryStringBuilder()
                .withPaginationIfNotEmpty(pagination)
                .withQueryIfNotEmpty('agendaItemId', agendaItemId)
                .withQueryIfNotEmpty('staleId', staleId)
                .build(`${this.apiUrl}/${id}/meeting/elections`);

            const response = await this.axiosClient.get(url);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async storeReference(id: string, electionId: string, agendaItemId?: string): Promise<IActivityToElectionReference> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        ArgumentValidator.notNullOrEmpty(electionId, `electionId`);

        try {

            const url = new QueryStringBuilder()
                .withQueryIfNotEmpty('agendaItemId', agendaItemId)
                .build(`${this.apiUrl}/${id}/meeting/reference/${electionId}`);

            const response = await this.axiosClient.put(url);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async storeKey(id: string, encryptionKey: IEncryptionKey): Promise<void> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        ArgumentValidator.notNullOrEmpty(encryptionKey, `encryptionKey`);

        try {

            const url = new QueryStringBuilder()
                .build(`${this.apiUrl}/${id}/meeting/encryption/storeKey`);

            const response = await this.axiosClient.post(url, encryptionKey);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async listMotionDocumentTypes(): Promise<IMotionDocumentType[]> {

        try {

            const url = new QueryStringBuilder()
                .build(`${this.apiUrl}/motioning/documentTypes`);

            const response = await this.axiosClient.get(url);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async allowTemporaryMotioningAccess(id: string, path: string): Promise<ITemporaryMotioningAccessTicket> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(path, `path`);

        try {

            const url = new QueryStringBuilder()
                .build(`${this.apiUrl}/${id}/motioning/temporaryAccess`);

            const response = await this.axiosClient.post(url, {
                path
            });

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public getExportLink(id: string, type: string, timezone: string): string {

        return `${this.baseUrl}${this.apiUrl}/${id}/meeting/export?type=${type}&timezone=${timezone}`;
    }
}
